import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Progress,
  Box,
  IconButton,
  Flex,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../context/AuthProvider';
import { axiosWrapper } from '../../hooks/axios';
import { useLimitedToast } from '../../hooks/useLimitedToast';
import { Survey, SurveyQuestion as SurveyQuestionType } from './types';
import SurveyQuestion from './SurveyQuestion';
import {
  isOtherOptionSelected,
  getOtherText,
} from './utils';

interface SurveyModalProps {
  survey: Survey;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const getStorageKey = (surveyId: string, userId: string) => `survey_draft_${surveyId}_${userId}`;

const saveToLocalStorage = (surveyId: string, userId: string, responses: Record<string, any>) => {
  try {
    const key = getStorageKey(surveyId, userId);
    const data = {
      responses,
      timestamp: Date.now(),
      surveyId,
      userId
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save survey responses to localStorage:', error);
  }
};

const loadFromLocalStorage = (surveyId: string, userId: string): Record<string, any> | null => {
  try {
    const key = getStorageKey(surveyId, userId);
    const data = localStorage.getItem(key);
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    
    // check if the data is for the correct survey and user
    if (parsed.surveyId !== surveyId || parsed.userId !== userId) {
      return null;
    }
    
    // we won't pull old data
    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
    if (Date.now() - parsed.timestamp > maxAge) {
      clearLocalStorage(surveyId, userId);
      return null;
    }
    
    return parsed.responses || {};
  } catch (error) {
    console.warn('Failed to load survey responses from localStorage:', error);
    return null;
  }
};

const clearLocalStorage = (surveyId: string, userId: string) => {
  try {
    const key = getStorageKey(surveyId, userId);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear survey responses from localStorage:', error);
  }
};

const SurveyModal: React.FC<SurveyModalProps> = ({
  survey,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const user = useContext(AuthContext);
  const { toast } = useLimitedToast({ maxToasts: 2 });
  
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasDraftData, setHasDraftData] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const { isOpen: isRestoreDialogOpen, onOpen: onRestoreDialogOpen, onClose: onRestoreDialogClose } = useDisclosure();
  
  const isMountedRef = useRef(true);

  // Filter questions relevant to user's roles
  const relevantQuestions = survey.questions.filter(question => {
    if (question.target_roles.length === 0) return true;
    
    return question.target_roles.some(role => {
      const roleStr = role.toString();
      // If target role is 'all', show to everyone
      if (roleStr === 'all') return true;
      // Otherwise check if user has this specific role
      return user?.roles?.map(r => r.toString()).includes(roleStr) || false;
    });
  });

  const totalSteps = relevantQuestions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // autosave responses to localStorage
  const autoSave = useCallback(() => {
    if (user?.pk && Object.keys(responses).length > 0) {
      saveToLocalStorage(survey.id.toString(), user.pk.toString(), responses);
    }
  }, [responses, survey.id, user?.pk]);

  // debounceautosave when responses change
  useEffect(() => {
    const timeoutId = setTimeout(autoSave, 1000); // 1 second delay
    return () => clearTimeout(timeoutId);
  }, [autoSave]);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (isOpen) {
      setCurrentStep(0);
      setResponses({});
      setErrors({});
      setHasSubmitted(false);
      setHasDraftData(false);
      
      // check for user's draft responses from local storage
      if (user?.pk) {
        const draftData = loadFromLocalStorage(survey.id.toString(), user.pk.toString());
        if (draftData && Object.keys(draftData).length > 0) {
          setHasDraftData(true);
          setShowRestoreDialog(true);
          onRestoreDialogOpen();
        }
      }
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [isOpen, survey, user?.pk, onRestoreDialogOpen]);

  const validateCurrentQuestion = () => {
    const currentQuestion = relevantQuestions[currentStep];
    if (!currentQuestion) return true;

    const response = responses[currentQuestion.id];
    
    if (currentQuestion.is_required) {
      if (currentQuestion.question_type === 'numeric') {
        if (response === undefined || response === null || response === '') {
          setErrors(prev => ({ ...prev, [currentQuestion.id]: t('survey.questionRequired') }));
          return false;
        }
        const numValue = typeof response === 'number' ? response : parseInt(response, 10);
        if (isNaN(numValue) || numValue < 0) {
          setErrors(prev => ({ ...prev, [currentQuestion.id]: t('survey.invalidNumber') || 'Please enter a valid non-negative number' }));
          return false;
        }
      } else if (response === undefined || response === null || response === '') {
        setErrors(prev => ({ ...prev, [currentQuestion.id]: t('survey.questionRequired') }));
        return false;
      }
      
      // for multiple choice, check if array is empty or object with empty selected
      if (Array.isArray(response) && response.length === 0) {
        setErrors(prev => ({ ...prev, [currentQuestion.id]: t('survey.questionRequired') }));
        return false;
      }
      
      if (
        typeof response === 'object' &&
        response !== null &&
        !Array.isArray(response) &&
        response.selected &&
        response.selected.length === 0
      ) {
        setErrors(prev => ({ ...prev, [currentQuestion.id]: t('survey.questionRequired') }));
        return false;
      }
      
    }
    
    // check if "other" selected with no text provided
    if (
      currentQuestion.allow_other_option &&
      (currentQuestion.question_type === 'single_choice' ||
       currentQuestion.question_type === 'multiple_choice')
    ) {
      const hasOther = isOtherOptionSelected(
        response,
        currentQuestion.question_type
      );
      
      if (hasOther) {
        const otherText = getOtherText(response, currentQuestion.question_type);
        if (!otherText || otherText.trim() === '') {
          setErrors(prev => ({ ...prev, [currentQuestion.id]: t('survey.pleaseProvideAnswer') }));
          return false;
        }
      }
    }

    // Clear error if validation passes
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[currentQuestion.id];
      return newErrors;
    });

    return true;
  };

  const handleNext = () => {
    if (!validateCurrentQuestion()) return;
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleResponseChange = (questionId: number, value: any) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
    
    // Clear error when user starts typing
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };


  const handleRestoreDraft = () => {
    if (user?.pk) {
      const draftData = loadFromLocalStorage(survey.id.toString(), user.pk.toString());
      if (draftData) {
        const restoredResponses: Record<string, any> = {};
        
        Object.keys(draftData).forEach(key => {
          restoredResponses[key] = draftData[key];
        });
        
        setResponses(restoredResponses);
        setHasDraftData(false);
        toast({
          title: t('survey.draftRestored'),
          description: t('survey.previousAnswersRestored'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    }
    onRestoreDialogClose();
  };

  const handleDiscardDraft = () => {
    if (user?.pk) {
      clearLocalStorage(survey.id.toString(), user.pk.toString());
    }
    setHasDraftData(false);
    onRestoreDialogClose();
  };

  const canCloseModal = () => {
    if (survey.can_be_skipped) {
      return true;
    }
    
    return hasSubmitted;
  };

  const handleClose = () => {
    if (canCloseModal()) {
      onClose();
    } else {
      toast({
        title: t('survey.cannotCloseSurvey'),
        description: t('survey.pleaseAnswerRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentQuestion()) return;

    setIsSubmitting(true);
    
    try {
      await axiosWrapper(
        `/api/v1/surveys/surveys/${survey.id}/submit_response/`,
        'post',
        { responses }
      );

      // Clear localStorage after successful submission
      if (user?.pk) {
        clearLocalStorage(survey.id.toString(), user.pk.toString());
      }

      // mark survey as submitted
      setHasSubmitted(true);

      toast({
        title: t('survey.submittedSuccessfully'),
        description: t('survey.thankYouFeedback'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Survey submission error:', error);
      
      if (isMountedRef.current) {
        toast({
          title: t('survey.submissionFailed'),
          description: error.response?.data?.error || t('survey.tryAgainLater'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  // Clamp step in case relevantQuestions shrinks between renders (e.g. survey prop change)
  const safeStep = relevantQuestions.length > 0
    ? Math.min(currentStep, relevantQuestions.length - 1)
    : 0;
  const currentQuestion = relevantQuestions[safeStep];

  if (!currentQuestion) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="4xl" 
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(8px)" />
      <ModalContent
        bg="white"
        borderRadius={{ base: "none", sm: "2xl" }}
        boxShadow={{ base: "none", sm: "0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)" }}
        maxH={{ base: "100vh", sm: "90vh" }}
        minH={{ base: "100vh", sm: "90vh" }}
        overflow="hidden"
        border={{ base: "none", sm: "1px solid" }}
        borderColor="gray.100"
        mx={{ base: 0, sm: 4 }}
        my={{ base: 0, sm: 4 }}
      >
        <ModalHeader
          bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          color="white"
          borderTopRadius={{ base: "none", sm: "2xl" }}
          pb={{ base: 4, sm: 6 }}
          pt={{ base: 4, sm: 6 }}
          px={{ base: 4, sm: 6 }}
        >
          <VStack align="start" spacing={{ base: 3, sm: 4 }}>
            <Flex justify="space-between" align="center" w="100%">
              <VStack align="start" spacing={1} flex={1} pr={2}>
                <Text fontSize={{ base: "lg", sm: "xl", md: "2xl" }} fontWeight="700" letterSpacing="-0.02em">
                  {survey.title}
                </Text>
                <Text fontSize={{ base: "xs", sm: "sm" }} opacity={0.9} fontWeight="400">
                  {survey.description}
                </Text>
              </VStack>
              <IconButton
                aria-label="Close survey"
                icon={<CloseIcon />}
                variant="ghost"
                color="white"
                _hover={{ bg: 'whiteAlpha.200', transform: 'scale(1.05)' }}
                _active={{ transform: 'scale(0.95)' }}
                onClick={handleClose}
                size="md"
                borderRadius="full"
                transition="all 0.2s"
                flexShrink={0}
              />
            </Flex>
            
            <Box w="100%">
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="500" opacity={0.9}>
                  {t('survey.progress')}
                </Text>
                <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="600" opacity={0.9}>
                  {safeStep + 1} of {totalSteps}
                </Text>
              </Flex>
              <Progress
                value={progress}
                bg="whiteAlpha.200"
                borderRadius="full"
                size="lg"
                sx={{
                  '& > div': {
                    background: 'linear-gradient(90deg, #ffecd2 0%, #fcb69f 100%)',
                  }
                }}
              />
            </Box>
          </VStack>
        </ModalHeader>

        <ModalBody 
          p={{ base: 4, sm: 6, md: 8 }} 
          maxH={{ base: "calc(100vh - 200px)", sm: "60vh" }} 
          overflowY="auto" 
          bg="gray.50"
          display="flex"
          flexDirection="column"
        >
          <VStack spacing={{ base: 4, sm: 6 }} align="stretch" flex={1} w="100%">
            <Box flex={1} display="flex" flexDirection="column" minH="350px">
              <SurveyQuestion
                question={currentQuestion}
                value={responses[currentQuestion.id]}
                onChange={(value) => handleResponseChange(currentQuestion.id, value)}
                error={errors[currentQuestion.id]}
                userRoles={user?.roles?.map(role => role.toString()) || []}
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter
          bg="white"
          borderBottomRadius={{ base: "none", sm: "2xl" }}
          px={{ base: 4, sm: 6, md: 8 }}
          py={{ base: 4, sm: 6 }}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <HStack justify="space-between" w="100%" spacing={{ base: 2, sm: 3 }}>
            <Button
              leftIcon={<ChevronLeftIcon />}
              onClick={handlePrevious}
              isDisabled={safeStep === 0}
              variant="outline"
              colorScheme="gray"
              size="lg"
              borderRadius={{ base: "lg", sm: "xl" }}
              fontWeight="500"
              _hover={{ 
                bg: 'gray.50',
                borderColor: 'gray.300',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.2s"
              flex={{ base: 1, sm: "none" }}
            >
              <Text display={{ base: "none", sm: "inline" }}>{t('survey.previous')}</Text>
              <Text display={{ base: "inline", sm: "none" }}>{t('survey.previous')}</Text>
            </Button>

            <HStack spacing={{ base: 2, sm: 3 }}>
              {safeStep < totalSteps - 1 ? (
                <Button
                  rightIcon={<ChevronRightIcon />}
                  onClick={handleNext}
                  bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  color="white"
                  size="lg"
                  borderRadius={{ base: "lg", sm: "xl" }}
                  fontWeight="600"
                  _hover={{ 
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)'
                  }}
                  _active={{ transform: 'translateY(0)' }}
                  isLoading={isSubmitting}
                  transition="all 0.2s"
                  flex={{ base: 1, sm: "none" }}
                >
                  {t('survey.next')}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  bg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                  color="white"
                  size="lg"
                  borderRadius={{ base: "lg", sm: "xl" }}
                  fontWeight="600"
                  _hover={{ 
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(240, 147, 251, 0.4)'
                  }}
                  _active={{ transform: 'translateY(0)' }}
                  isLoading={isSubmitting}
                  loadingText={t('survey.submitting')}
                  transition="all 0.2s"
                  flex={{ base: 1, sm: "none" }}
                >
                  <Text display={{ base: "none", sm: "inline" }}>{t('survey.submitSurvey')}</Text>
                  <Text display={{ base: "inline", sm: "none" }}>{t('survey.submit')}</Text>
                </Button>
              )}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
      
      {/* Restore Draft Dialog */}
      <AlertDialog
        isOpen={isRestoreDialogOpen}
        onClose={onRestoreDialogClose}
        leastDestructiveRef={undefined}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('survey.restorePreviousAnswers')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('survey.foundPreviousAnswers')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={handleDiscardDraft} variant="outline" mr={3}>
                {t('survey.startFresh')}
              </Button>
              <Button onClick={handleRestoreDraft} colorScheme="blue">
                {t('survey.restoreAnswers')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default SurveyModal; 